import { Accordion, AccordionActions, AccordionDetails, AccordionSummary } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import Alert from 'core/components/Alert'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import TextField from 'core/components/validatedForm/TextField'
import PlusButton from 'core/elements/button/PlusButton'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import Theme from 'core/themes/model'
import { customValidator } from 'core/utils/fieldValidators'
import { remove, update } from 'ramda'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { isNilOrEmpty } from 'utils/fp'
import { trackEvent } from 'utils/tracking'
import { listResmgrHosts } from '../../actions'
import { resmgrHostsSelector } from '../../selectors'
import { getGpuModels } from './helpers'
import HostNetworkInterfacesTable from './HostNetworkInterfacesTable'
import HostNetworkGpuConfig from './HostNetworkGpuConfig'

const useStyles = makeStyles<Theme>((theme) => ({
  sameLine: {
    display: 'grid',
    gap: 8,
    alignItems: 'center',
    gridTemplateColumns: 'max-content max-content auto',
    marginLeft: theme.spacing(-1.5),
  },
  error: {
    marginLeft: 16,
    color: theme.components.badge.error.color,
  },
  errorText: {
    color: theme.components.badge.error.color,
  },
  hostConfigName: {
    width: '340px',
  },
  container: {
    display: 'grid',
    gap: theme.spacing(3),
    width: '100%',
  },
  accordion: {
    border: `1px solid ${theme.components.table.border}`,
    boxShadow: 'none',

    '& .MuiAccordionSummary-content.Mui-expanded': {
      margin: 0,
    },
  },
  accordionSummary: {
    backgroundColor: theme.palette.background.default,
    minHeight: '48px',

    // override MUI styles
    '&.MuiAccordionSummary-root.Mui-expanded': {
      minHeight: '48px',
      borderBottom: `1px solid ${theme.components.table.border}`,
    },
  },
  accordionDetails: {
    '&.MuiAccordionDetails-root': {
      padding: theme.spacing(4, 3),
    },
  },
  accordionActions: {
    padding: theme.spacing(2),
    justifyContent: 'flex-start',
    borderTop: `1px solid ${theme.components.table.border}`,
  },
}))

export const defaultInterfaceConfig = (defaultTrue = false) => ({
  physnet: '',
  physnetName: '',
  management: defaultTrue,
  tunnel: defaultTrue,
  hostLiveness: defaultTrue,
  imageLibrary: defaultTrue,
  vmConsole: defaultTrue,
  physicalNetworkLabel: '',
  GpuPciPassthroughs: [],
})

const validLabelString = customValidator((label) => {
  if (!label) {
    return true
  }
  return /^[a-zA-Z0-9_-]+$/i.test(label)
}, 'Physical network label must only consist of alphanumeric characters, hyphens(-), and underscores(_)')

export default function HostNetworkConfiguration({
  params,
  updateParams,
  config,
  idx,
  hasError,
  existingHostNetworkConfig,
  setHostConfigErrorIndexes,
  virtualNetworkLabelValidator,
}) {
  const classes = useStyles()
  useListAction(listResmgrHosts, {
    params,
  })
  const [gpuModels, setGpuModels] = useState([])
  const [expanded, setExpanded] = useState(hasError || isNilOrEmpty(existingHostNetworkConfig))

  const hosts = useSelector(resmgrHostsSelector)
  const usedHostConfigIds = hosts?.map((host) => host?.hostconfig_id)?.filter((id) => !!id)
  const [enableGpuPassthrough, setEnableGpuPassthrough] = useState<boolean>(
    !!config?.gpuPciPassthroughs?.length,
  )

  useEffect(() => {
    const makeCalls = async () => {
      const models = await getGpuModels()
      setGpuModels(models)
    }
    makeCalls()
  }, [])

  const updateField = useCallback(
    (value, prop) => {
      const updatedConfig = {
        ...config,
        [prop]: value,
      }
      const updatedConfigs = update(idx, updatedConfig, params.hostNetworkConfigs)
      updateParams({ hostNetworkConfigs: updatedConfigs })
    },
    [params.hostNetworkConfigs, config, idx],
  )

  const deleteConfig = useCallback(() => {
    const updatedConfigs = remove(idx, 1, params.hostNetworkConfigs)
    updateParams({ hostNetworkConfigs: updatedConfigs })
    trackEvent(`Cluster Blueprint - Clicked Remove Host Network Configuration`)
  }, [params.hostNetworkConfigs, idx])

  const addNetworkInterface = useCallback(() => {
    const updatedConfig = {
      ...config,
      networkInterfaces: [
        ...config.networkInterfaces,
        defaultInterfaceConfig(!config.networkInterfaces.length),
      ],
    }
    const updatedConfigs = update(idx, updatedConfig, params.hostNetworkConfigs)
    updateParams({ hostNetworkConfigs: updatedConfigs })
    trackEvent(`Cluster Blueprint - Clicked Add Network Interface`)
  }, [params.hostNetworkConfigs, config, idx])

  const updateNetworkInterface = useCallback(
    (value, property, interfaceIndex) => {
      let updatedConfig = { ...config }
      if (
        ['management', 'vmConsole', 'imageLibrary', 'tunnel', 'hostLiveness'].includes(property)
      ) {
        updatedConfig = {
          ...config,
          networkInterfaces: config?.networkInterfaces?.map((iface, index) => ({
            ...iface,
            [property]: index !== interfaceIndex ? false : true,
          })),
        }
      } else {
        const currentInterface = config?.networkInterfaces?.[interfaceIndex]
        const updatedInterface = {
          ...currentInterface,
          [property]: value,
        }
        updatedConfig = {
          ...config,
          networkInterfaces: update(interfaceIndex, updatedInterface, config?.networkInterfaces),
        }
      }

      const updatedConfigs = update(idx, updatedConfig, params.hostNetworkConfigs)
      updateParams({ hostNetworkConfigs: updatedConfigs })
    },
    [params.hostNetworkConfigs, config, idx],
  )

  const removeNetworkInterface = useCallback(
    (interfaceIndex) => {
      const updatedConfig = {
        ...config,
        networkInterfaces: remove(interfaceIndex, 1, config?.networkInterfaces),
      }
      const updatedConfigs = update(idx, updatedConfig, params.hostNetworkConfigs)
      updateParams({ hostNetworkConfigs: updatedConfigs })
      trackEvent(`Cluster Blueprint - Clicked Remove Network Interface`)
    },
    [params.hostNetworkConfigs, config, idx],
  )

  const trafficAssigned = useCallback(
    (property) => {
      const assigned = config.networkInterfaces?.some((iface) => !!iface[property])
      return assigned
    },
    [config],
  )

  const physicalNetworkLabelsAdded = useMemo(
    () => config?.networkInterfaces.some((iface) => !!iface?.physicalNetworkLabel),
    [config],
  )

  const newPhysicalNetworkLabelsAdded = useMemo(
    () => config?.networkInterfaces.some((iface) => iface?.physnetName === ''),
    [config],
  )

  const allTrafficAssigned =
    trafficAssigned('management') &&
    trafficAssigned('vmConsole') &&
    trafficAssigned('imageLibrary') &&
    trafficAssigned('tunnel') &&
    trafficAssigned('hostLiveness')

  // For some reason when removing one of the configs, params
  // in second arg of customValidator is messed up, so use useMemo here
  const uniqueHostConfigNameValidator = useMemo(
    () =>
      customValidator((name) => {
        const matchingConfigNames = params.hostNetworkConfigs
          ?.map((config) => config.name)
          ?.filter((configName) => configName === name)
        return matchingConfigNames.length < 2
      }, 'Each host network configuration must have a unique name'),
    [params],
  )

  const isUsedHostConfig = useMemo(
    () => usedHostConfigIds?.length > 0 && usedHostConfigIds.includes(config?.id),
    [config, usedHostConfigIds],
  )

  const passthroughList = useMemo(
    () =>
      gpuModels.map((passthrough) => ({
        label: passthrough.model,
        value: passthrough.name,
      })),
    [gpuModels],
  )

  useEffect(() => {
    if (hasError) {
      setExpanded(true)
    }
  }, [hasError])

  const onAccordionChange = useCallback((_, nextExpanded) => {
    setExpanded(nextExpanded)
    setHostConfigErrorIndexes([])
  }, [])

  return (
    <Accordion expanded={expanded} onChange={onAccordionChange} className={classes.accordion}>
      <AccordionSummary
        className={classes.accordionSummary}
        expandIcon={<FontAwesomeIcon size="xs">chevron-down</FontAwesomeIcon>}
      >
        <Text component="span" variant="caption1">
          #{idx + 1} Host Configuration {config?.name && ` - ${config?.name}`}
        </Text>
      </AccordionSummary>
      <AccordionDetails className={classes.accordionDetails}>
        <div className={classes.container}>
          <div>
            <TextField
              id={`hostNetworkConfigs.${idx}.name`}
              label="Host Configuration Name"
              onChange={(value) => updateField(value, 'name')}
              value={config?.name}
              placeholder="Name"
              validations={[uniqueHostConfigNameValidator]}
              className={classes.hostConfigName}
              required
            />
          </div>
          <HostNetworkInterfacesTable
            params={params}
            config={config}
            idx={idx}
            isUsedHostConfig={isUsedHostConfig}
            updateNetworkInterface={updateNetworkInterface}
            removeNetworkInterface={removeNetworkInterface}
            addNetworkInterface={addNetworkInterface}
            virtualNetworkLabelValidator={virtualNetworkLabelValidator}
            validLabelString={validLabelString}
            existingNetworkInterfaces={existingHostNetworkConfig?.networkInterfaces}
          />
          {!physicalNetworkLabelsAdded && allTrafficAssigned && (
            <div className={classes.sameLine}>
              <FontAwesomeIcon className={classes.error}>circle-exclamation</FontAwesomeIcon>
              <Text variant="body2" className={classes.errorText}>
                At least one physical network label name should be provided
              </Text>
            </div>
          )}
          {!allTrafficAssigned && (
            <div className={classes.sameLine}>
              <FontAwesomeIcon className={classes.error}>circle-exclamation</FontAwesomeIcon>
              <Text variant="body2" className={classes.errorText}>
                All system traffic types must be assigned within each host network configuration!
              </Text>
            </div>
          )}
          {isUsedHostConfig && newPhysicalNetworkLabelsAdded && (
            <Alert
              variant="warning"
              message={
                <>
                  Adding a new NIC here will also propagate to already configured hosts using this
                  Host Network Configuration. To avoid any disruption to active workloads, please
                  ensure that this NIC is present on all such hosts.
                </>
              }
            />
          )}
          <HostNetworkGpuConfig
            params={params}
            config={config}
            idx={idx}
            enableGpuPassthrough={enableGpuPassthrough}
            setEnableGpuPassthrough={setEnableGpuPassthrough}
            existingHostNetworkConfig={existingHostNetworkConfig}
            passthroughList={passthroughList}
            updateField={updateField}
            isUsedHostConfig={isUsedHostConfig}
          />
        </div>
      </AccordionDetails>
      <AccordionActions className={classes.accordionActions}>
        <PlusButton
          disabled={isUsedHostConfig}
          onClick={() => deleteConfig()}
          icon="minus-circle"
          label="Delete Configuration"
          tooltip={isUsedHostConfig ? 'This configuration is in-use and cannot be deleted' : ''}
          data-testid="delete-host-configuration-button"
        />
      </AccordionActions>
    </Accordion>
  )
}
